import React, {
  useState,
  useEffect,
  useRef
} from "react";

import {
  Map,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  Polyline
} from "react-leaflet";

import {
  Modal,
  Button
} from "react-bootstrap";

import L from "leaflet";

import "leaflet/dist/leaflet.css";

import { formatProperCaseWithUnderscores } from "../../../../_metronic";

const colors = [
  "#1E90FF",
  "#28A745",
  "#DC3545",
  "#FD7E14",
  "#6F42C1",
  "#20C997",
  "#E83E8C",
  "#17A2B8",
  "#FFC107",
  "#6610F2",
  "#FF5733",
  "#C70039",
  "#900C3F",
  "#2ECC71",
  "#3498DB",
  "#F39C12",
  "#8E44AD",
  "#16A085",
  "#D35400",
  "#2C3E50",
  "#FF6B6B",
  "#4ECDC4",
  "#556270",
  "#C7F464",
  "#FFCC5C",
  "#96CEB4",
  "#6C5B7B",
  "#355C7D",
  "#F67280",
  "#99B898",
  "#BADA55",
  "#7FDBFF",
  "#FF851B",
  "#85144b",
  "#3D9970",
  "#111111",
  "#AAAAAA",
  "#39CCCC",
  "#01FF70",
  "#F012BE"
];

const createCustomIcon = (color) => {
  return new L.DivIcon({
    className: "custom-marker",
    html: `
      <div style="
        background:${color};
        width:18px;
        height:18px;
        border-radius:50%;
        border:3px solid white;
        box-shadow:0 0 6px rgba(0,0,0,0.4);
      "></div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9]
  });
};

const PsActivityMap = ({ mapData }) => {
  const [showExpandedMap, setShowExpandedMap] =
    useState(false);

  const mapRef = useRef(null);

  useEffect(() => {
    if (showExpandedMap && mapRef.current) {
      setTimeout(() => {
        if (
          mapRef.current.leafletElement
        ) {
          // react-leaflet v2
          mapRef.current.leafletElement.invalidateSize();
        } else if (
          mapRef.current.invalidateSize
        ) {
          // react-leaflet v3+
          mapRef.current.invalidateSize();
        }
      }, 300);
    }
  }, [showExpandedMap]);

  if (!mapData || !mapData.length) {
    return (
      <div className="dashboard-card p-4 text-center">
        No map data available
      </div>
    );
  }

  const userColors = {};

  mapData.forEach((item) => {
    if (!userColors[item.psName]) {
      userColors[item.psName] =
        colors[
        Object.keys(userColors).length %
        colors.length
        ];
    }
  });

  const center = [
    parseFloat(mapData[0].latitude),
    parseFloat(mapData[0].longitude)
  ];

  const userPaths = {};

  mapData.forEach((item, index) => {
    const user = item.psName;

    if (!userPaths[user]) {
      userPaths[user] = [];
    }

    let lat = parseFloat(item.latitude);
    let lng = parseFloat(item.longitude);

    // avoid overlapping markers
    lat = lat + index * 0.00002;
    lng = lng + index * 0.00002;

    userPaths[user].push([lat, lng]);
  });

  const renderMap = (height = "500px") => (
    <Map
      ref={mapRef}
      center={center}
      zoom={13}
      style={{
        height,
        width: "100%"
      }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {mapData.map((item, index) => {
        let lat = parseFloat(item.latitude);
        let lng = parseFloat(item.longitude);

        lat = lat + index * 0.00002;
        lng = lng + index * 0.00002;

        return (
          <Marker
            key={`${item.jobId}-${index}`}
            position={[lat, lng]}
            icon={createCustomIcon(
              userColors[item.psName]
            )}
          >
            <Tooltip>
              <div style={{ minWidth: "220px" }}>
                <strong>
                  {item.psName}
                </strong>

                <br />

                {item.formattedHouseName}

                <br />

                <strong>
                  Job Type:
                </strong>{" "}
                {formatProperCaseWithUnderscores(
                  item.jobType
                )}

                <br />

                <strong>
                  Status:
                </strong>{" "}
                {formatProperCaseWithUnderscores(
                  item.currentStatus
                )}
              </div>
            </Tooltip>

            <Popup>
              <div style={{ minWidth: "250px" }}>
                <h6
                  style={{
                    fontWeight: 600
                  }}
                >
                  {item.psName}
                </h6>

                <div className="mb-2">
                  <strong>
                    Property:
                  </strong>

                  <br />

                  {
                    item.formattedHouseName
                  }
                </div>

                <div className="mb-2">
                  <strong>
                    Job Type:
                  </strong>

                  <br />

                  {formatProperCaseWithUnderscores(
                    item.jobType
                  )}
                </div>

                <div>
                  <strong>
                    Status:
                  </strong>

                  <br />

                  {formatProperCaseWithUnderscores(
                    item.currentStatus
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {Object.keys(userPaths).map(
        (user) => (
          <Polyline
            key={user}
            positions={userPaths[user]}
            color={userColors[user]}
            weight={4}
            opacity={0.7}
          />
        )
      )}
    </Map>
  );

  return (
    <>
      <div className="dashboard-card">
        <div className="dashboard-card-header d-flex justify-content-between align-items-center">
          <span>
            Property Supervisors
            Activity Map
          </span>

          <Button
            variant="light"
            size="sm"
            onClick={() =>
              setShowExpandedMap(true)
            }
          >
          {/* <i className="flaticon-home-1 text-primary"></i> */}
          <i className="flaticon2-size mr-2"></i>
          </Button>
        </div>

        <div className="p-2">
          {renderMap("500px")}
        </div>

        <div className="map-legend">
          <h6 className="mb-3">
            Property Supervisors
          </h6>

          <div className="legend-container">
            {Object.keys(userColors).map((user) => (
              <div
                key={user}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px"
                }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    backgroundColor: userColors[user],
                    marginRight: "10px"
                  }}
                />

                <span>{user}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        show={showExpandedMap}
        onHide={() =>
          setShowExpandedMap(false)
        }
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Property Supervisors
            Activity Map
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: 0
          }}
        >
          {renderMap("80vh")}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PsActivityMap;